/**
 * People and history — `mockups/studio/client-detail.html`'s last section, less one
 * column.
 *
 * The mockup's own comment: "one region, three columns, no borders between them
 * (P8)." Two of the three are here. **Files left in session 24** for a tab of its own
 * (`FilesSection.tsx`, and `ClientDetailPage.tsx` `SECTIONS`): the column was six
 * lines and four numbers over five actions that had no screen anywhere in the studio,
 * and a per-file list with a control on every row does not fit in one third of a
 * `RegionCols`. Its summary went with it, so one fact is still stated in one place.
 *
 * P3 in both remaining columns: the seats are folded behind a count, and the activity
 * list shows the newest few with the rest behind a line. The audit log is 49 rows for
 * Casaturca and the mockup prints three.
 *
 * Session 24 changed three things here, all from the second manual pass:
 *
 *   - **R2-14.** A pending invitation was drawn with no action at all — the studio
 *     could see a seat held by a mistyped address and could do nothing about it, and
 *     neither could the client. `clients_cancel_invitation` is the capability and
 *     this is its second door; the client's own is `pages/client/settings`.
 *   - **R2-07.** The activity timeline's stamps go through `stamp` rather than
 *     `datetime`, so a time of day says which clock it is on. The zone is still the
 *     reader's browser (`format/useFormat.ts`).
 *   - **R2-15.** A seat whose person never gave a name is answered with their
 *     address as the name (`clients/queries.ts` `memberViewOf`), and this list
 *     printed the name over the address — the same string twice. `hasOwnName` in
 *     `packages/shared` is the one reader of that fallback; the client's own People
 *     card had the identical cell and the identical bug.
 *
 * The second manual pass then found two more, and both were the read model's rather
 * than this file's — the page states what it was given:
 *
 *   - **R3-02.** The panel filtered after paging, so invoices, payments, calls and
 *     retainer changes never appeared at all.
 *   - **R2-34.** A line read `content_plan 01a07145-… — content_plans_update_item`
 *     and named nobody. `title` is a sentence now and `actor` is a field; `noteOf`
 *     below is the whole of this file's part in it.
 */

import { StudioActivityItemSchema, hasOwnName, type StudioClientDetailView } from '@fareast/shared'
import { useState } from 'react'
import { z } from 'zod'

import { messageOf } from '../../../api/problem.js'
import { useStudioAction } from '../../../api/studio.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Fold } from '../../../components/portal/Disclosure.js'
import { Kv, KvRow } from '../../../components/portal/Kv.js'
import { Push, Row, Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Dialog } from '../../../components/portal/Overlay.js'
import { Region, RegionCols, RegionHead } from '../../../components/portal/Region.js'
import { Notice, Pill, Tag } from '../../../components/portal/Status.js'
import { Timeline, TimelineItem } from '../../../components/portal/Timeline.js'
import { DateText, Muted, Prose, Small, Xs } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'

export const PEOPLE_ID = 'people'

/** How many activity rows sit above the fold. The mockup draws three. */
const ACTIVITY_ROWS = 4

/** One pending invitation, as `clients_get_detail` answers it. */
type PendingInvitation = StudioClientDetailView['contacts']['invitations'][number]

/** One line of the timeline, as `clients_get_detail` answers it. */
type ActivityItem = StudioClientDetailView['activity'][number]

/** The timeline, with every schema default applied. See `PeopleSection` for why. */
const ActivitySchema = z.array(StudioActivityItemSchema)

/**
 * The second line of a timeline item: who did it, and whatever else the read model
 * had to say.
 *
 * Session 24 (R2-34). Every line on this panel used to name nobody — the audit row
 * carries an actor and the read model was not reading it, so the studio's own record
 * of who did what could not answer the question. `actor` is null for a row whose
 * person has gone (`readmodels/studio-client-detail.ts` `actorOf` says which of the
 * two kinds of null this is, and answers "the portal" for the other one), and a line
 * with nothing under it is what it was before.
 */
function noteOf(item: ActivityItem): string | undefined {
  const parts = [item.actor, item.note === '' ? null : item.note].filter(
    (one): one is string => one !== null,
  )
  return parts.length === 0 ? undefined : parts.join(' · ')
}

export type PeopleSectionProps = {
  readonly detail: StudioClientDetailView
}

export function PeopleSection({ detail }: PeopleSectionProps) {
  const format = useFormat()
  const { contacts } = detail
  /*
   * The timeline is **parsed** rather than read, the same way `OpenItemsSection`
   * reads its own slice and for the same reason: `useStudioRead` types the answer
   * from the *handler's* return type and not from the schema, so nothing on this path
   * applies a schema default. `actor` is new (R2-34) and is `undefined` in a recording
   * taken before it — which is not null, and would print the word "undefined" under
   * every line. One parse turns it into the null `noteOf` already answers.
   */
  const activity = ActivitySchema.parse(detail.activity)

  /** The invitation whose withdrawal is being confirmed. One at a time. */
  const [cancelling, setCancelling] = useState<PendingInvitation | null>(null)

  const recent = activity.slice(0, ACTIVITY_ROWS)
  const rest = activity.slice(ACTIVITY_ROWS)

  return (
    <Section id={PEOPLE_ID}>
      <SectionHead title="People and history" note="Everything else" />

      <Region>
        <RegionCols>
          {/* ------------------------------------------------------- contacts */}
          <Stack>
            <RegionHead
              level={3}
              title="Contacts"
              note={`${format.number(contacts.seats_used)} of ${format.number(
                contacts.seat_limit,
              )} seats`}
              pushNote
            />

            <Prose>
              {contacts.members.length === 0
                ? 'Nobody holds a seat on this account yet.'
                : `${format.qty(contacts.members.length, {
                    one: 'person holds a seat',
                    other: 'people hold seats',
                  })}. An owner or an admin is who may approve money.`}
            </Prose>

            {contacts.invitations.map((invitation) => (
              <Row key={invitation.invitation_id}>
                <Pill state="need">Pending</Pill>
                <Small>
                  <Muted>
                    {invitation.email} — the invite expires{' '}
                    <DateText value={invitation.expires_at} />
                  </Muted>
                </Small>
                <Push />
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Cancel the invitation to ${invitation.email}`}
                  onClick={() => {
                    setCancelling(invitation)
                  }}
                >
                  Cancel
                </Button>
              </Row>
            ))}

            {contacts.members.length === 0 ? null : (
              <Fold
                summary={`The ${format.qty(contacts.members.length, {
                  one: 'seat',
                  other: 'seats',
                })}`}
              >
                <Kv bare>
                  {contacts.members.map((member) => (
                    <KvRow
                      key={member.member_id}
                      k={
                        <>
                          {member.name} <Tag>{member.role}</Tag>
                        </>
                      }
                      /*
                       * R2-15: `memberViewOf` answers the address when nobody ever
                       * gave a name, so for an invited seat the key and the value
                       * were the same string printed twice. `hasOwnName` is the one
                       * reader of that fallback and the client's People card asks it
                       * too.
                       */
                      v={
                        <Small>
                          <Muted>
                            {hasOwnName(member) ? member.email : ''}
                            {member.timezone === null
                              ? ''
                              : `${hasOwnName(member) ? ' · ' : ''}${member.timezone}`}
                          </Muted>
                        </Small>
                      }
                    />
                  ))}
                </Kv>
              </Fold>
            )}
          </Stack>

          {/* ------------------------------------------------------- activity */}
          <Stack>
            <RegionHead level={3} title="Activity" note="last 30 days" pushNote />

            {activity.length === 0 ? (
              <Prose>
                <Muted>Nothing has happened in the portal this month.</Muted>
              </Prose>
            ) : (
              <>
                <Timeline>
                  {recent.map((item, index) => (
                    <TimelineItem
                      key={`${item.at}-${item.kind}-${String(index)}`}
                      date={<DateText value={item.at} style="stamp" />}
                      title={item.title}
                      note={noteOf(item)}
                    />
                  ))}
                </Timeline>

                {rest.length === 0 ? null : (
                  <Fold
                    summary={`The other ${format.qty(rest.length, {
                      one: 'event',
                      other: 'events',
                    })}`}
                  >
                    <Timeline>
                      {rest.map((item, index) => (
                        <TimelineItem
                          key={`${item.at}-${item.kind}-${String(index)}`}
                          date={<DateText value={item.at} style="stamp" />}
                          title={item.title}
                          note={noteOf(item)}
                        />
                      ))}
                    </Timeline>
                  </Fold>
                )}
              </>
            )}

            <Xs>
              <Muted>Correspondence lives in Gmail. Only portal events appear here.</Muted>
            </Xs>
          </Stack>
        </RegionCols>
      </Region>

      {cancelling === null ? null : (
        <CancelInvitationDialog
          client={detail.client}
          invitation={cancelling}
          onClose={() => {
            setCancelling(null)
          }}
        />
      )}
    </Section>
  )
}

/* ------------------------------------------------- taking an invitation back */

/**
 * *Cancel* on a pending row — session 24 (R2-14), the studio's door on
 * `clients_cancel_invitation`.
 *
 * A plain `Dialog` rather than a `ConfirmDialog`: `Overlay.tsx` reserves that one for
 * money, and nothing is committed here. What it does do is end a link somebody is
 * holding, with no undo but a second invitation, so the consequence is a sentence and
 * the destructive button names the address — which is the thing that was probably
 * mistyped, and the only name this person has, because nobody has accepted.
 */
function CancelInvitationDialog({
  client,
  invitation,
  onClose,
}: {
  readonly client: string
  readonly invitation: PendingInvitation
  readonly onClose: () => void
}) {
  const cancel = useStudioAction('clients_cancel_invitation')

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Cancel the invitation"
    >
      <Stack gap="tight">
        <Small>
          The link we sent {invitation.email} stops working and the seat it was holding is free
          again. Nobody is emailed about it, and the account can invite them — or the address they
          meant — straight afterwards.
        </Small>
        {cancel.isError ? (
          <Notice need role="alert">
            {messageOf(cancel.error)}
          </Notice>
        ) : null}
        <ButtonRow>
          <Button
            variant="primary"
            disabled={cancel.isPending}
            onClick={() => {
              void cancel
                .mutateAsync({ client, invitation_id: invitation.invitation_id })
                .then(onClose)
                .catch(() => undefined)
            }}
          >
            Cancel the invitation to {invitation.email}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              onClose()
            }}
          >
            Leave it waiting
          </Button>
        </ButtonRow>
      </Stack>
    </Dialog>
  )
}
